/** @license @dairyfarm/eval-web v3.0.0
* Copyright 2020 Texas A&M Center for Applied Technology, 
* Texas A&M Engineering Experiment Station, The Texas A&M 
* University System.
* 
* This source code/schema and any derivative work is Texas 
* A&M Engineering Experiment Station intellectual property 
* and protected by copyright law. Usage is restricted solely 
* to the FARM Animal Care and Environmental Stewardship 
* programs administered by the National Milk Producers 
* Federation. Dissemination of this source code and/or 
* derivative work creation is strictly prohibited without 
* express written permission from the Texas A&M Engineering 
* Experiment Station.
*/
import { DFSValueType } from '../../react/components/form/services/form';
import { Map } from 'immutable';
import React from 'react';
import { createTransform } from 'redux-persist';
import { ActionType, getType } from 'typesafe-actions';
import { AppAction, cancelPublish, FollowUp, ImmediateFollowUpData, followUpSortComparator } from '..';
import { convertPointerToPathComponents } from '../../utils/runtime';
import { acknowledgePublishError, acknowledgePublishSuccess } from '../interface';
import { FollowUpVisitData, openFollowUpEditor, submitFollowUp, syncFollowUps, updateFollowUp, FollowUpData, getFollowUpsForFacility } from './actions';
import moment from 'moment';

export type FollowUpsMap = Map<string, FollowUp[]>;
export type VisitsMap = Map<string, Partial<FollowUpVisitData>>;

export interface FollowUpsState {
  items: FollowUpsMap;
  visits: VisitsMap;
  loading: boolean;
  fetchingAll: boolean;
  fetchingForFacility: boolean;
  /**
   * All facilities for which **all** follow-ups have been fetched. Any follow-ups fetched for these
   * facilities should be preserved when follow-ups are re-fetched in bulk.
   */
  facilitiesFetched: string[],
  lastFetch?: string | null;
  error?: Error;
  grouping: 'single' | 'grouped';

  submitState: SubmitState & {
    submittedFollowUp?: FollowUpData;
  };
}

const updateFollowUpReducer: (state: FollowUpsState, action: ActionType<typeof updateFollowUp>) => FollowUpsState = (
  state,
  action
) => {
  if (!action.meta)
    // We cannot update a follow-up if we don't know which one
    return state;

  let nextVisitsState: VisitsMap;
  // go from JSON Pointer to path component array
  const targetPath = [action.meta, ...convertPointerToPathComponents(action.payload.path).slice(1)]; // slice to remove prefixed 'visit' path
  switch (action.payload.op) {
    case 'add':
    case 'replace':
      if (/^-$/.test(targetPath[targetPath.length - 1])) {
        // Means add next array item
        const listPath = targetPath.slice(0, targetPath.length - 1);
        const list: DFSValueType[] = state.items.getIn(listPath);
        nextVisitsState = state.visits.setIn(listPath, [...list, action.payload.value]);
      } else nextVisitsState = state.visits.setIn(targetPath, action.payload.value);
      break;
    case 'copy':
      nextVisitsState = state.visits.setIn(
        targetPath,
        state.visits.getIn(convertPointerToPathComponents(action.payload.from).slice(1))
      );
      break;
    case 'remove':
      nextVisitsState = state.visits.removeIn(targetPath);
      break;
    case 'move':
      const fromPath = state.visits.getIn(convertPointerToPathComponents(action.payload.from).slice(1));
      nextVisitsState = state.visits.setIn(targetPath, fromPath);
      nextVisitsState = nextVisitsState.removeIn(fromPath);
      break;
    default:
      nextVisitsState = state.visits;
  }

  return {
    ...state,
    visits: nextVisitsState,
  };
};

interface SubmitState {
  submitting: boolean;
  error?: string | JSX.Element;
  step?: 'submitting' | 'submitted';
}

function submitStateReducer(
  state: SubmitState = {
    submitting: false,
  },
  action: AppAction
): SubmitState {
  switch (action.type) {
    case getType(submitFollowUp.request):
      return {
        submitting: true,
        step: 'submitting',
      };
    case getType(submitFollowUp.success):
      return {
        submitting: true,
        step: 'submitted',
      };
    case getType(submitFollowUp.failure):
      // TODO: Determine appropriate message to display
      let message: string | JSX.Element;
      switch (action.payload.status) {
        case 400:
        case 500:
          message = 'Something went wrong. Please contact technical support for assistance.';
          break;
        case 409:
          message = React.createElement(
            'span',
            undefined,
            'Submission not possible. The visit date must be within 24 hours of the deadline. Please contact ',
            React.createElement(
              'a',
              {
                href:
                  'mailto:dairyfarm@nmpf.org?subject=IAP Follow-Up Visit Submission Error&body=Greetings,%0D%0A%0D%0AI use the FARM Eval app. I am unable to submit a visit record resolving a scheduled IAP follow-up because my visit was not within 24 hours of the deadline. %0D%0A%0D%0AI would like to request additional information regarding the steps that I need to take to resolve this IAP.',
              },
              'FARM Support'
            ),
            ' for assistance.'
          );
          break;
        case 403:
          message = 'Only certified evaluators are allowed to publish a follow-up visit.';
          break;
        case 404:
          message = 'The server is temporarily unavailable. Please try again later';
          break;
        default:
          message = 'Something went wrong when submitting. Please try again later.';
          break;
      }

      return {
        ...state,
        error: message,
      };
    case getType(cancelPublish):
    case getType(acknowledgePublishError):
    case getType(acknowledgePublishSuccess):
      return {
        submitting: false,
      };

    default:
      return state;
  }
}

export const followUpsReducer: (state: FollowUpsState | undefined, action: AppAction) => FollowUpsState = (
  state: FollowUpsState = {
    loading: false,
    fetchingAll: true,
    fetchingForFacility: false,
    grouping: 'single',
    items: Map({}),
    visits: Map({}),
    submitState: {
      submitting: false,
    },
    facilitiesFetched: []
  },
  action
) => {

  switch (action.type) {
    case getType(syncFollowUps.request):
      return {
        ...state,
        fetchingAll: true
      }

    case getType(syncFollowUps.success):

      let newItems = Map(action.payload);
      let existingItems = Map(state.items);

      try {
        if (!state.facilitiesFetched) {
          state.facilitiesFetched = [];
        }
        state.facilitiesFetched.forEach(facilityId => {

        const newItemsForFacility: FollowUp[] = newItems.get(facilityId) || [];
        const existingItemsForFacility: FollowUp[] = existingItems.get(facilityId) || [];
        const added: string[] = [];

        const deduped = [...newItemsForFacility, ...existingItemsForFacility]
          .map(item => {
            if (!added.includes(item.data.id)) {
              added.push(item.data.id);
              return item;
            }
          })
          .filter(i => i)
          .sort((a, b) => followUpSortComparator((a as FollowUp).data, (b as FollowUp).data));


          newItems = newItems.set(facilityId, deduped as FollowUp[]);
        })
      } catch(e) {
        console.log('ERROR COMBINING FOLLOW-UPS', e);
      }

      return {
        ...state,
        items: newItems,
        lastFetch: moment().toISOString(),
        fetchingAll: false
      };

    case getType(syncFollowUps.failure):
      return {
        ...state,
        fetchingAll: false
      }
    case getType(openFollowUpEditor):
      let newVisits = state.visits;
      if (!state.visits.get(action.payload.id)) {
        newVisits = state.visits.set(action.payload.id, {});
      }

      return {
        ...state,
        visits: newVisits,
      };

    case getType(getFollowUpsForFacility.request):
      return {
        ...state,
        fetchingForFacility: true
      }
      
    // if all follow-ups for a facility have been fetched, merge it's follow-ups in subsequent
    // requests instead of overwriting them
    case getType(getFollowUpsForFacility.success):
      return {
        ...state,
        items: state.items.set(action.payload.id, action.payload.followUps),
        fetchingForFacility: false,
        facilitiesFetched: Array.from(new Set([...state.facilitiesFetched, action.payload.id]))
      }
    
    case getType(getFollowUpsForFacility.failure):
      return {
        ...state,
        fetchingForFacility: false
      }

    case getType(updateFollowUp):
      return updateFollowUpReducer(state, action);

    case getType(submitFollowUp.request):
    case getType(submitFollowUp.failure):
    case getType(cancelPublish):
    case getType(acknowledgePublishError):
      return {
        ...state,
        submitState: submitStateReducer(state.submitState, action)
      };
    case getType(submitFollowUp.success): // update the facility record accordingly
      return {
        ...state,
        submitState: {
          ...submitStateReducer(state.submitState, action),
          submittedFollowUp: action.payload
        },
      };
    case getType(acknowledgePublishSuccess):
      if (!state.submitState.submitting) return state;  // shared action between eval and follow-up submission

      // waiting until confirmed risks state not updating if refreshed, but should be replaced with network copy in that case
      const updatedFollowUp = state.submitState.submittedFollowUp!; 

      let nextItems = state.items;

      const followUpIndex = nextItems
        .get(updatedFollowUp.facility)
        ?.findIndex((fu: FollowUp) => fu.data.id === updatedFollowUp.id);

      const targetPath = [updatedFollowUp.facility, followUpIndex];

      // If it's resolved we don't need it anymore
      if (updatedFollowUp.resolved) {
        nextItems = nextItems.removeIn(targetPath);
      } else {
        nextItems = nextItems.setIn(targetPath.concat('data'), updatedFollowUp);
      }

      return {
        ...state,
        items: nextItems,
        submitState: submitStateReducer(state.submitState, action),
        visits: state.visits.remove(updatedFollowUp.id),
      };

    default:
      break;
  }

  return state;
};

/**
 * Everything must be stringified in order to be persisted in browser storage and transformed back to
 * the relevant data structures when retrieved from storage
 */
export const followUpsTransform = createTransform<FollowUpsState, string>(
  (inboundState) => {
    return JSON.stringify(inboundState);
  },
  (outboundState) => {
    const js: Exclude<FollowUpsState, 'items' | 'submitState'> & { items: { [id: string]: FollowUp[] } } = JSON.parse(
      outboundState
    );
    return {
      ...js,
      items: Map(js.items),
      visits: Map(js.visits),
      submitState: {
        submitting: false,
      }
    };
  },
  {
    whitelist: ['followUps'],
  }
);

export function nearestDeadline(data: ImmediateFollowUpData): string {
  if (!data.immediate_resolved) return data.immediate_follow_up_date;

  if (!data.week_resolved) return data.week_follow_up_date;

  if (!data.month_resolved) return data.month_follow_up_date;

  return data.month_three_follow_up_date;
}
